import { RefObject } from "react";
import useForm from "../../../../../custom-hooks/form/useForm";
import useFormWarning from "../../../../../custom-hooks/form/useFormWarning";
import useInputChangeHandler from "../../../../../custom-hooks/form/handlers/useInputChangeHandler";
import useCheckboxChangeHandler from "../../../../../custom-hooks/form/handlers/useCheckboxChangeHandler";
import useSelectChangeHandler from "../../../../../custom-hooks/form/handlers/useSelectChangeHandler";
import { testIPv4 } from "../../../../../shared-functions/regex";

type UseNetworkCreateFormProps = {
  nameRef: RefObject<HTMLElement>;
  regionRef: RefObject<HTMLElement>;
  dnsRef: RefObject<HTMLElement>;
  gatewayIPRef: RefObject<HTMLElement>;
  subnetPoolRef: RefObject<HTMLElement>;
};

const useNetworkCreateForm = ({
  nameRef,
  regionRef,
  dnsRef,
  gatewayIPRef,
  subnetPoolRef,
}: UseNetworkCreateFormProps) => {
  const { formWarning, showFormWarning, hideFormWarning } = useFormWarning();

  const { formData, error, handleChange } = useForm({
    validations: [
      {
        field: "name",
        requiredMessage: "Please enter a name for your network.",
        ref: nameRef,
      },
      {
        field: "region",
        requiredMessage: "Please select a region.",
        ref: regionRef,
      },
      {
        field: "dnsNameservers",
        custom: {
          validateFunction: (value: string, formData: any) =>
            !formData.completeCreate || value,
          errorMessage: "Please provide a dns value.",
        },
        ref: dnsRef,
      },
      {
        field: "gatewayIp",
        custom: {
          validateFunction: (value: string, formData: any) =>
            !formData.completeCreate || !value || testIPv4(value),
          errorMessage:
            "Please enter a valid ip for gateway. Example: 192.168.0.1",
        },
        ref: gatewayIPRef,
      },
      {
        field: "subnetPool",
        custom: {
          validateFunction: (value: string, formData: any) =>
            !formData.completeCreate || formData.ipVersion === 4 || value,
          errorMessage: "Please choose a subnet pool",
        },
        ref: subnetPoolRef,
      },
    ],
    initialState: {
      portSecurity: true,
    },
  });

  const handleInputChange = useInputChangeHandler({
    handleChange,
    hideFormWarning,
  });
  const handleCheckboxChange = useCheckboxChangeHandler({
    handleChange,
    hideFormWarning,
  });
  const handleSelectChange = useSelectChangeHandler({
    handleChange,
    hideFormWarning,
  });

  return {
    formWarning,
    showFormWarning,
    error,
    formData,
    handleInputChange,
    handleSelectChange,
    handleCheckboxChange,
  };
};

export default useNetworkCreateForm;
